import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import {ISubmitTraderFormFieldInput} from 'constants/Api';

import {ITraderFieldLike} from '../tradingFormJourneyUtils';
import {
  ErrorBanner,
  FormField,
  SelectInput,
  TextInput,
} from '../shared/components';
import {
  lshFilterFieldDependants,
  lshFlattenTraderFormFieldsForEdit,
  lshGetTraderFormCustomGroupFields,
  lshGetTraderFormFieldCurrentValue,
  lshPrepareTraderFieldSubmission,
  lshResolveEntityTypeTitleFromFields,
  lshSetTraderFormCustomGroupFields,
  lshValidateTraderFormFields,
} from '../tradingFormInlineUtils';

interface ITradingFormSectionInlineProps {
  title: string;
  description?: string;
  fields: ITraderFieldLike[];
  onSubmit: (fields: ISubmitTraderFormFieldInput[]) => Promise<void>;
  isSubmitting?: boolean;
  errorMessage?: string | null;
  entityTypeTitle?: string;
  onRemoveGroup?: (group: ITraderFieldLike) => Promise<void>;
}

export interface TradingFormSectionInlineHandle {
  submit: () => Promise<boolean>;
  reset: () => void;
}

const lshCloneFields = (fields: ITraderFieldLike[]) =>
  JSON.parse(JSON.stringify(fields || [])) as ITraderFieldLike[];

const lshRemoveGroupFromFields = (
  fields: ITraderFieldLike[],
  groupId: string,
): ITraderFieldLike[] => {
  const next = lshCloneFields(fields);
  const walk = (items: ITraderFieldLike[]): ITraderFieldLike[] =>
    items
      .filter((item) => String(item.id) !== groupId)
      .map((item) => {
        if (String((item as any).__typename || '') !== 'CustomGroup')
          return item;
        const children = walk(lshGetTraderFormCustomGroupFields(item));
        lshSetTraderFormCustomGroupFields(item, children);
        return item;
      });

  return walk(next);
};

const TradingFormSectionInline = forwardRef<
  TradingFormSectionInlineHandle,
  ITradingFormSectionInlineProps
>(
  (
    {
      title,
      description,
      fields,
      onSubmit,
      isSubmitting,
      errorMessage,
      entityTypeTitle,
      onRemoveGroup,
    },
    ref,
  ) => {
    const [localFields, setLocalFields] = useState<ITraderFieldLike[]>(
      () => lshCloneFields(fields) || [],
    );
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [removingGroupId, setRemovingGroupId] = useState<string | null>(null);

    const flatFields = useMemo(
      () => lshFlattenTraderFormFieldsForEdit(localFields),
      [localFields],
    );

    const refresh = useCallback(() => {
      setLocalFields(lshCloneFields(fields));
      setFieldErrors({});
    }, [fields]);

    const clearFieldError = (fieldId: string | number) => {
      setFieldErrors((prev) => {
        if (!prev[String(fieldId)]) return prev;
        const next = {...prev};
        delete next[String(fieldId)];
        return next;
      });
    };

    const handleInputUpdate = (
      field: ITraderFieldLike,
      value: string | boolean | string[],
    ) => {
      const typeName = String((field as any).__typename || '');
      switch (typeName) {
        case 'TradingFormTextInput': {
          const resolved = String(value);
          (field as any).textInputValue = resolved;
          (field as any).value = resolved;
          break;
        }
        case 'TradingFormSingleChoiceOption': {
          const resolved = String(value);
          (field as any).singleChoiceOptionValue = resolved;
          (field as any).value = resolved;
          break;
        }
        case 'TradingFormMultiChoiceOption': {
          const values = Array.isArray(value) ? value : [String(value)];
          (field as any).multiChoiceOptionValues = values;
          (field as any).values = values;
          break;
        }
        case 'TradingFormCheckBox': {
          const resolved = Boolean(value);
          (field as any).checkBoxValue = resolved;
          (field as any).value = resolved;
          break;
        }
        default:
          break;
      }

      if (field.id !== undefined && field.id !== null) {
        clearFieldError(field.id);
      }
      setLocalFields([...localFields]);
    };

    const handleSubmit = useCallback(async (): Promise<boolean> => {
      const resolvedEntityTypeTitle = lshResolveEntityTypeTitleFromFields(
        localFields,
        entityTypeTitle || '',
      );
      const validationErrors = lshValidateTraderFormFields(localFields, {
        entityTypeTitle: resolvedEntityTypeTitle || entityTypeTitle,
      });
      if (Object.keys(validationErrors).length > 0) {
        setFieldErrors(validationErrors);
        return false;
      }

      setFieldErrors({});
      const submission = lshPrepareTraderFieldSubmission(localFields);
      try {
        await onSubmit(submission);
        return true;
      } catch {
        return false;
      }
    }, [entityTypeTitle, localFields, onSubmit]);

    useImperativeHandle(
      ref,
      () => ({
        submit: handleSubmit,
        reset: refresh,
      }),
      [handleSubmit, refresh],
    );

    const handleRemoveGroup = async (group: ITraderFieldLike) => {
      if (!onRemoveGroup || isSubmitting) return;
      const groupId = String(group.id);
      setRemovingGroupId(groupId);
      try {
        await onRemoveGroup(group);
        setLocalFields((prev) => lshRemoveGroupFromFields(prev, groupId));
        setFieldErrors((prev) => {
          if (!Object.keys(prev).length) return prev;
          const next = {...prev};
          const removedIds = lshFlattenTraderFormFieldsForEdit(
            lshGetTraderFormCustomGroupFields(group),
          ).map((field) => String(field.id));
          removedIds.forEach((id) => delete next[id]);
          delete next[groupId];
          return next;
        });
      } catch {
        // Parent sets section-level error state.
      } finally {
        setRemovingGroupId(null);
      }
    };

    const renderField = (field: ITraderFieldLike): React.ReactNode => {
      if (!lshFilterFieldDependants(field, flatFields)) return null;

      const fieldId = String(field.id);
      const error = fieldErrors[fieldId];
      const typeName = String((field as any).__typename || '');

      switch (typeName) {
        case 'TradingFormText':
          return null;
        case 'TradingFormTextInput': {
          const required = (
            (field as any).textInputValidationTypes ||
            (field as any).validationTypes ||
            []
          ).some((validation: string) =>
            ['TEXT', 'ID_NUMBER', 'NUMBER', 'ENTITY_REG_NUMBER'].includes(
              String(validation),
            ),
          );
          return (
            <FormField
              key={fieldId}
              label={
                (field as any).textInputTitle || (field as any).title || 'Field'
              }
              required={required}
              error={error}
            >
              <TextInput
                type="text"
                value={String(lshGetTraderFormFieldCurrentValue(field) || '')}
                onChange={(event) =>
                  handleInputUpdate(field, event.target.value || '')
                }
              />
            </FormField>
          );
        }
        case 'TradingFormSingleChoiceOption': {
          const options = ((field as any).singleChoiceOptionOptions ||
            (field as any).options ||
            []) as Array<{title: string; value: string}>;
          return (
            <FormField
              key={fieldId}
              label={
                (field as any).singleChoiceOptionTitle ||
                (field as any).title ||
                'Select'
              }
              error={error}
            >
              <SelectInput
                value={String(lshGetTraderFormFieldCurrentValue(field) || '')}
                onChange={(event) =>
                  handleInputUpdate(field, event.target.value)
                }
                placeholder="Select an option"
                options={options.map((option) => ({
                  label: option.title,
                  value: option.value,
                }))}
              />
            </FormField>
          );
        }
        case 'TradingFormMultiChoiceOption': {
          const options = ((field as any).multiChoiceOptionOptions ||
            (field as any).options ||
            []) as Array<{title: string; value: string}>;
          const values = ((field as any).multiChoiceOptionValues ||
            (field as any).values ||
            []) as string[];
          return (
            <FormField
              key={fieldId}
              as="fieldset"
              label={
                (field as any).multiChoiceOptionTitle ||
                (field as any).title ||
                'Select all that apply'
              }
              error={error}
            >
              <div className="checkbox-grid">
                {options.map((option) => (
                  <label key={option.value} className="checkbox-option">
                    <input
                      type="checkbox"
                      checked={values.includes(option.value)}
                      onChange={(event) => {
                        const nextValues = new Set(values);
                        if (event.target.checked) nextValues.add(option.value);
                        else nextValues.delete(option.value);
                        handleInputUpdate(field, Array.from(nextValues));
                      }}
                    />
                    <span>{option.title}</span>
                  </label>
                ))}
              </div>
            </FormField>
          );
        }
        case 'TradingFormCheckBox':
          return (
            <label
              key={fieldId}
              className={`checkbox-option${error ? ' has-error' : ''}`}
            >
              <input
                type="checkbox"
                checked={Boolean(lshGetTraderFormFieldCurrentValue(field))}
                onChange={(event) =>
                  handleInputUpdate(field, event.target.checked)
                }
              />
              <span>
                {(field as any).checkBoxTitle ||
                  (field as any).title ||
                  'Option'}
              </span>
              {error ? <span className="form-error">{error}</span> : null}
            </label>
          );
        case 'TradingFormFileUpload':
          return (
            <div key={fieldId} className="file-field-info">
              <span className="form-label">
                {(field as any).fileUploadTitle ||
                  (field as any).title ||
                  'File upload'}
              </span>
              <p className="form-note">
                Attachments are managed from the documents section in this flow.
              </p>
            </div>
          );
        case 'CustomGroup': {
          const groupFields = lshGetTraderFormCustomGroupFields(field);
          if (groupFields.length === 0) return null;
          const isRemoving = removingGroupId === fieldId;
          return (
            <div key={fieldId} className="form-group-block">
              {(field as any).customGroupTitle ? (
                <div className="form-group-block__header">
                  <h4>{(field as any).customGroupTitle}</h4>
                  {onRemoveGroup ? (
                    <button
                      type="button"
                      className="link-button btn-secondary is-danger"
                      onClick={() => {
                        void handleRemoveGroup(field);
                      }}
                      disabled={Boolean(isSubmitting) || isRemoving}
                    >
                      {isRemoving ? 'Removing...' : 'Remove'}
                    </button>
                  ) : null}
                </div>
              ) : null}
              <div className="form-group-grid">
                {groupFields.map((child) => renderField(child))}
              </div>
            </div>
          );
        }
        default:
          return null;
      }
    };

    return (
      <div className="company-modal__panel company-modal__panel--inline">
        <header>
          <h3>{title}</h3>
          <button
            type="button"
            className="link-button"
            onClick={refresh}
            disabled={isSubmitting}
          >
            Reset
          </button>
        </header>
        {description ? (
          <p className="company-modal__description">{description}</p>
        ) : null}

        <div className="company-modal__form">
          {localFields.map((field) => renderField(field))}
        </div>

        <ErrorBanner message={errorMessage} />
      </div>
    );
  },
);

TradingFormSectionInline.displayName = 'TradingFormSectionInline';

export default TradingFormSectionInline;
